import { NzTableComponent } from "ng-zorro-antd/table";

export interface HdTableScrollConfig {
  x?: string;
  y?: string;
}

const DEFAULT_FLUID_COL_WIDTH = 150;

export function getTableViewportWidth(
  nzTable: NzTableComponent,
  hostClass = "hd-table-container",
): number {
  if (!nzTable || !nzTable.tableMainElement) {
    return 0;
  }
  const tableMain = nzTable.tableMainElement.nativeElement as HTMLElement;
  const scrollBody = tableMain.querySelector(
    ".ant-table-body",
  ) as HTMLElement;
  if (scrollBody && scrollBody.clientWidth > 0) {
    return scrollBody.clientWidth;
  }
  const wrapper = tableMain.closest(".ant-table-wrapper") as HTMLElement;
  if (wrapper && wrapper.clientWidth > 0) {
    return wrapper.clientWidth;
  }
  const host = tableMain.closest(`.${hostClass}`) as HTMLElement;
  if (host && host.clientWidth > 0) {
    return host.clientWidth;
  }
  return 0;
}

function parsePx(value: string): number {
  if (!value) {
    return 0;
  }
  const parsed = parseInt(String(value).replace(/px/i, ""), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** 克隆表头/表体测量未被 scroll 压缩前的自然列宽 */
function measureElementNaturalWidth(element: HTMLElement): number {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.position = "absolute";
  clone.style.left = "-99999px";
  clone.style.top = "0";
  clone.style.visibility = "hidden";
  clone.style.width = "max-content";
  clone.style.tableLayout = "auto";
  document.body.appendChild(clone);
  const width = clone.scrollWidth;
  document.body.removeChild(clone);
  return width;
}

/** 汇总表头 th 上声明的列宽（style.width / nzWidth） */
function measureHeaderDeclaredColWidth(tableMain: HTMLElement): number {
  const ths = Array.from(
    tableMain.querySelectorAll(".ant-table-thead > tr > th"),
  ) as HTMLElement[];
  if (!ths.length) {
    return 0;
  }
  let sum = 0;
  let hasFluidCol = false;
  ths.forEach((th) => {
    const declared = parsePx(th.style.width);
    if (declared > 0) {
      sum += declared;
      return;
    }
    hasFluidCol = true;
    sum += th.scrollWidth || th.offsetWidth || DEFAULT_FLUID_COL_WIDTH;
  });
  return hasFluidCol ? 0 : sum;
}

export function measureTableColWidth(nzTable: NzTableComponent): number {
  if (!nzTable || !nzTable.tableMainElement) {
    return 0;
  }
  const tableMain = nzTable.tableMainElement.nativeElement as HTMLElement;
  const tables = Array.from(
    tableMain.querySelectorAll(
      ".ant-table-header table, .ant-table-body table, .cdk-virtual-scroll-content-wrapper table",
    ),
  ) as HTMLElement[];

  let max = tables.reduce(
    (width, table) => Math.max(width, table.scrollWidth || 0),
    0,
  );

  const viewportWidth = getTableViewportWidth(nzTable);
  // 未开启 scroll.x 时表格会被压进容器，scrollWidth 不可靠，需测自然列宽
  if (viewportWidth <= 0 || max <= viewportWidth) {
    const headerTable = tableMain.querySelector(
      ".ant-table-header table",
    ) as HTMLElement;
    const bodyTable = tableMain.querySelector(
      ".ant-table-body table",
    ) as HTMLElement;
    if (headerTable) {
      max = Math.max(max, measureElementNaturalWidth(headerTable));
    }
    if (bodyTable) {
      max = Math.max(max, measureElementNaturalWidth(bodyTable));
    }
    max = Math.max(max, measureHeaderDeclaredColWidth(tableMain));
  }

  return max;
}

/** 无数据或列宽未超出容器时不启用横向 scroll */
export function resolveAutoTableScroll(
  nzTable: NzTableComponent,
  hasData: boolean,
  colWidth?: number,
): HdTableScrollConfig | null {
  if (!hasData) {
    return null;
  }
  const width =
    colWidth != null && colWidth > 0
      ? colWidth
      : measureTableColWidth(nzTable);
  if (width <= 0) {
    return null;
  }
  const viewportWidth = getTableViewportWidth(nzTable);
  if (viewportWidth > 0) {
    return width > viewportWidth ? { x: `${Math.ceil(width)}px` } : null;
  }
  if (typeof window !== "undefined" && width > window.innerWidth - 32) {
    return { x: `${Math.ceil(width)}px` };
  }
  return null;
}

/** 取 ng-zorro 分体表头 / 表体滚动容器（含虚拟滚动） */
export function getNzTableScrollElements(
  nzTable: NzTableComponent,
): { header: HTMLElement | null; body: HTMLElement | null } {
  if (!nzTable) {
    return { header: null, body: null };
  }
  const nz = nzTable as any;
  let header = (nz.tableHeaderNativeElement as HTMLElement) || null;
  let body =
    (nz.mixTableBodyNativeElement as HTMLElement) ||
    (nz.tableBodyNativeElement as HTMLElement) ||
    null;
  const main =
    nz.tableMainElement &&
    (nz.tableMainElement.nativeElement as HTMLElement);
  if (main) {
    if (!header) {
      header = main.querySelector(".ant-table-header") as HTMLElement;
    }
    if (!body) {
      body =
        (main.querySelector(
          "cdk-virtual-scroll-viewport.ant-table-body",
        ) as HTMLElement) ||
        (main.querySelector(".ant-table-body") as HTMLElement);
    }
  }
  return { header, body };
}

/** 一次性把表体 scrollLeft 同步到表头 */
export function syncNzTableHeaderBodyScrollLeft(
  nzTable: NzTableComponent,
): void {
  const els = getNzTableScrollElements(nzTable);
  if (!els.header || !els.body) {
    return;
  }
  if (els.header.scrollLeft !== els.body.scrollLeft) {
    els.header.scrollLeft = els.body.scrollLeft;
  }
}

/**
 * 监听表体横向滚动并同步表头。
 * 返回 unbind；DOM 未就绪时返回 null（调用方可重试）。
 */
export function bindNzTableHeaderBodyScrollSync(
  nzTable: NzTableComponent,
): (() => void) | null {
  const els = getNzTableScrollElements(nzTable);
  if (!els.header || !els.body) {
    return null;
  }
  const header = els.header;
  const body = els.body;
  const onScroll = () => {
    if (header.scrollLeft !== body.scrollLeft) {
      header.scrollLeft = body.scrollLeft;
    }
  };
  body.addEventListener("scroll", onScroll);
  onScroll();
  return () => {
    body.removeEventListener("scroll", onScroll);
  };
}
